import type * as React from 'react'
import { cn } from '../lib/utils'

/**
 * A table scrolls sideways inside its own wrapper. `bordered` gives it the in-page panel surface
 * (white, hairline, 6px radius) for tables that sit straight on the paper page rather than in a panel.
 */
export function Table({
  className,
  bordered = false,
  ...props
}: React.ComponentProps<'table'> & { bordered?: boolean }) {
  return (
    <div
      className={cn(
        'relative w-full overflow-x-auto',
        bordered && 'rounded-md border border-line bg-surface',
      )}
    >
      <table data-slot="table" className={cn('relative w-full text-sm', className)} {...props} />
    </div>
  )
}

export function TableHeader(props: React.ComponentProps<'thead'>) {
  return <thead data-slot="table-header" {...props} />
}

export function TableBody(props: React.ComponentProps<'tbody'>) {
  return <tbody data-slot="table-body" {...props} />
}

export function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn('border-line border-b last:border-b-0', className)}
      {...props}
    />
  )
}

export function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'bg-paper px-3 py-2.5 text-left font-semibold text-[13px] text-ink-soft sm:px-4',
        className,
      )}
      {...props}
    />
  )
}

export function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn('px-3 py-3 align-middle sm:px-4', className)}
      {...props}
    />
  )
}
